/**
 * Admin Calendar View - Monthly calendar view for viewing attorney time data.
 *
 * Features:
 * - Attorney selector dropdown (same as EntriesView)
 * - Monthly summary card showing unexported/exported hours
 * - Calendar grid showing daily hours (adapted from attorney CalendarView)
 * - Click a day to navigate to EntriesView with filters pre-populated
 */

import { useState, useMemo } from "react";
import { ChevronsUpDown, Check } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { cn } from "@/shared/lib/utils";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import { useProfessionals, useCalendarTransactions } from "../hooks/useAdminData";
import { usePersistedState } from "../hooks/usePersistedState";
import styles from "./AdminViews.module.css";
import calendarStyles from "../../attorney/views/CalendarView/CalendarView.module.css";
import summaryStyles from "../../attorney/components/SummaryCards/SummaryCards.module.css";

// Nav arrow icons for month navigation
import navArrowLeftIcon from "@/assets/icons/navArrowLeft.svg";
import navArrowLeftWhiteIcon from "@/assets/icons/navArrowLeftWhite.svg";
import navArrowRightIcon from "@/assets/icons/navArrowRight.svg";
import navArrowRightWhiteIcon from "@/assets/icons/navArrowRightWhite.svg";

interface AdminCalendarViewProps {
  onNavigateToEntries: (date: Date, profId: string) => void;
}

export default function AdminCalendarView({
  onNavigateToEntries,
}: AdminCalendarViewProps) {
  const { darkMode } = useDarkMode();

  // Persisted state for selected attorney
  const [selectedProfId, setSelectedProfId] = usePersistedState<string | null>(
    "admin:calendar:professional",
    null
  );

  // Current month being displayed
  const [currentMonthDate, setCurrentMonthDate] = useState<Date>(new Date());

  // UI state for attorney dropdown
  const [attorneySearch, setAttorneySearch] = useState("");
  const [attorneyOpen, setAttorneyOpen] = useState(false);

  // Data fetching
  const { professionals, loading: profLoading } = useProfessionals({
    activeOnly: true,
  });
  const { transactions, loading: txLoading } = useCalendarTransactions(
    currentMonthDate,
    selectedProfId
  );

  // Filter professionals by search
  const filteredProfs = useMemo(() => {
    if (!attorneySearch) return professionals.slice(0, 10);
    const term = attorneySearch.toLowerCase();
    return professionals
      .filter(
        (p) =>
          p.full_name.toLowerCase().includes(term) ||
          p.email.toLowerCase().includes(term)
      )
      .slice(0, 10);
  }, [professionals, attorneySearch]);

  const selectedProfName = professionals.find(
    (p) => p.prof_id === selectedProfId
  )?.full_name;

  // Generate unique day key for aggregation
  const dayKey = (d: Date) =>
    `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

  // Compute daily sums from transactions
  const dailySums = useMemo(() => {
    const sums: Record<string, { unexported: number; exported: number }> = {};
    for (const tx of transactions) {
      // Parse date string without timezone conversion (avoid off-by-one error)
      // tx.date may be "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM:SS.sssZ"
      const datePart = tx.date.split("T")[0];
      const [year, month, day] = datePart.split("-").map(Number);
      const d = new Date(year, month - 1, day); // Local date
      const k = dayKey(d);
      sums[k] = sums[k] || { unexported: 0, exported: 0 };
      if (tx.exported) {
        sums[k].exported += tx.units;
      } else {
        sums[k].unexported += tx.units;
      }
    }
    return sums;
  }, [transactions]);

  // Compute monthly totals
  const monthlyStats = useMemo(() => {
    let unexported = 0;
    let exported = 0;
    for (const tx of transactions) {
      if (tx.exported) {
        exported += tx.units;
      } else {
        unexported += tx.units;
      }
    }
    return { unexported, exported };
  }, [transactions]);

  // Build 6x7 grid of days for the current month
  const calendarDays = useMemo(() => {
    const firstOfMonth = new Date(
      currentMonthDate.getFullYear(),
      currentMonthDate.getMonth(),
      1
    );
    const startDay = new Date(firstOfMonth);
    const startWeekday = startDay.getDay(); // 0 Sun .. 6 Sat
    // Start from Sunday before/at first of month
    startDay.setDate(startDay.getDate() - startWeekday);
    const days: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(startDay);
      d.setDate(startDay.getDate() + i);
      days.push(d);
    }
    return days;
  }, [currentMonthDate]);

  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const goToPreviousMonth = () => {
    const d = new Date(currentMonthDate);
    d.setMonth(d.getMonth() - 1);
    setCurrentMonthDate(d);
  };

  const goToNextMonth = () => {
    const d = new Date(currentMonthDate);
    d.setMonth(d.getMonth() + 1);
    setCurrentMonthDate(d);
  };

  const goToToday = () => {
    setCurrentMonthDate(new Date());
  };

  const handleDayClick = (day: Date) => {
    if (!selectedProfId) return;
    onNavigateToEntries(day, selectedProfId);
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-4 p-6 bg-background h-full",
        styles.adminViewWrapper
      )}
    >
      {/* Header row: Summary card (left) + Attorney dropdown (right) */}
      <div className="flex items-end gap-4">
        {/* Monthly Summary Card - using SummaryCards styling */}
        <div className={`${summaryStyles.card} ${summaryStyles.cardInline}`}>
          <div className={summaryStyles.block}>
            <div className={summaryStyles.dateText}>
              {currentMonthDate.toLocaleDateString(undefined, {
                month: "short",
                year: "numeric",
              })}
            </div>
            <div className={summaryStyles.textRow}>
              {monthlyStats.unexported.toFixed(1)} hours to export
            </div>
            <div className={summaryStyles.textRow}>
              {monthlyStats.exported.toFixed(1)} hours exported
            </div>
          </div>
        </div>

        {/* Attorney Dropdown */}
        <Popover open={attorneyOpen} onOpenChange={setAttorneyOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-[220px] justify-between">
              {selectedProfName || "Select attorney..."}
              <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0">
            <Command>
              <CommandInput
                placeholder="Search..."
                value={attorneySearch}
                onValueChange={setAttorneySearch}
              />
              <CommandList>
                <CommandEmpty>
                  {profLoading ? "Loading..." : "No attorneys found."}
                </CommandEmpty>
                <CommandGroup>
                  {filteredProfs.map((prof) => (
                    <CommandItem
                      key={prof.prof_id}
                      value={`${prof.full_name} ${prof.email}`}
                      onSelect={() => {
                        setSelectedProfId(prof.prof_id);
                        setAttorneyOpen(false);
                        setAttorneySearch("");
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedProfId === prof.prof_id
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col">
                        <span>{prof.full_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {prof.email}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Calendar Grid */}
      <div className="border border-border rounded-lg overflow-hidden flex-1 flex flex-col">
        {!selectedProfId ? (
          // Empty state - no attorney selected
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            Select an attorney to view their calendar
          </div>
        ) : txLoading ? (
          // Loading state
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            Loading...
          </div>
        ) : (
          // Calendar view
          <div className={calendarStyles.calendarView}>
            {/* Calendar Header */}
            <div className={calendarStyles.calendarHeader}>
              <span className={calendarStyles.calendarHeaderLabel}>
                {currentMonthDate.toLocaleDateString(undefined, {
                  month: "long",
                  year: "numeric",
                })}
              </span>
              <div className={calendarStyles.calendarHeaderButtonWrapper}>
                <button
                  className={calendarStyles.calendarPageButton}
                  onClick={goToPreviousMonth}
                >
                  <img
                    src={darkMode ? navArrowLeftWhiteIcon : navArrowLeftIcon}
                    alt="Previous"
                  />
                </button>
                <button
                  className={`${calendarStyles.calendarPageButton} ${calendarStyles.calendarTodayButton}`}
                  onClick={goToToday}
                >
                  Today
                </button>
                <button
                  className={calendarStyles.calendarPageButton}
                  onClick={goToNextMonth}
                >
                  <img
                    src={darkMode ? navArrowRightWhiteIcon : navArrowRightIcon}
                    alt="Next"
                  />
                </button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className={calendarStyles.calendarGrid}>
              {/* Weekday headers */}
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div key={day} className={calendarStyles.calendarWeekdayHeader}>
                  {day}
                </div>
              ))}

              {/* Day cells */}
              {calendarDays.map((currDay, index) => {
                const isCurrentMonth =
                  currDay.getMonth() === currentMonthDate.getMonth();
                const isWeekend =
                  currDay.getDay() === 0 || currDay.getDay() === 6;
                const key = dayKey(currDay);
                const unexported = (dailySums[key]?.unexported || 0).toFixed(1);
                const exported = (dailySums[key]?.exported || 0).toFixed(1);

                return (
                  <button key={index} onClick={() => handleDayClick(currDay)}>
                    <div
                      className={`${calendarStyles.calendarCard} ${
                        !isCurrentMonth
                          ? calendarStyles.calendarOutsideMonthCard
                          : ""
                      } ${isWeekend ? calendarStyles.calendarWeekendCard : ""}`}
                      data-today={isSameDay(currDay, new Date())}
                    >
                      <div className={calendarStyles.cardDate}>
                        {currDay.getDate()}
                      </div>
                      <div
                        className={`${calendarStyles.cardMetric} ${calendarStyles.cardMetricUnits}`}
                      >
                        {unexported} hrs to export
                      </div>
                      <div
                        className={`${calendarStyles.cardMetric} ${calendarStyles.cardMetricUnits}`}
                      >
                        {exported} hrs exported
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
